import { FormEvent, useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth-context';

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

interface GiftingFormProps {
  defaultShareCode?: string;
  onSuccess?: (result?: { sponsor_balance: number; teacher_balance: number }) => void;
}

export default function GiftingForm({ defaultShareCode = '', onSuccess }: GiftingFormProps) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const [shareCode, setShareCode] = useState(defaultShareCode);
  const [credits, setCredits] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    setShareCode(defaultShareCode);
  }, [defaultShareCode]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token) {
      setStatus('error');
      setMessage('Please sign in as a sponsor to gift credits.');
      return;
    }

    const parsedCredits = Number.parseInt(credits, 10);
    if (!shareCode.trim() || !Number.isFinite(parsedCredits) || parsedCredits <= 0) {
      setStatus('error');
      setMessage('Enter a share code and a positive credit amount.');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const payload = {
        shareCode: shareCode.trim(),
        amount: parsedCredits,
        typeOut: 'gift_send',
        typeIn: 'gift_receive',
        source: 'share_link',
        note: `Gift via share ${shareCode.trim()}`,
      };

      const response = await fetch(`${CORE_API}/api/v1/sponsors/gift-credits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(json?.error || 'Unable to gift credits');
      }

      setStatus('success');
      setMessage('Credits sent! Platform credits for keepsakes and emblems. Credits do not mint classroom slots.');
      if (onSuccess) {
        onSuccess({
          sponsor_balance: Number(json?.sponsor_balance || 0),
          teacher_balance: Number(json?.teacher_balance || 0),
        });
      }
      setCredits('');
    } catch (error) {
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Unable to gift credits');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 rounded-2xl bg-white/70 p-4 shadow-soft">
      <h3 className="text-lg font-semibold text-cocoa">Gift platform credits</h3>
      <p className="text-sm text-cocoa/80">
        Platform credits for keepsakes and emblems—purchased via credit packs and delivered without ever holding USD for teache
rs. Credits do not mint
        classroom slots.
      </p>
      <label className="block text-sm font-medium text-cocoa">
        Teacher share code
        <input
          className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-base text-cocoa focus:border-cocoa focus:outline-none"
          value={shareCode}
          onChange={(event) => setShareCode(event.target.value)}
          placeholder="e.g. PT-ABC123"
        />
      </label>
      <label className="block text-sm font-medium text-cocoa">
        Credits to send
        <input
          className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-base text-cocoa focus:border-cocoa focus:outline-none"
          value={credits}
          onChange={(event) => setCredits(event.target.value.replace(/[^0-9]/g, ''))}
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="500"
        />
      </label>
      <button
        type="submit"
        className="w-full rounded-xl bg-cocoa px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:bg-cocoa/60"
        disabled={status === 'loading'}
      >
        {status === 'loading' ? 'Sending…' : 'Send credits'}
      </button>
      {message && (
        <p className={`text-sm ${status === 'error' ? 'text-brick' : 'text-cocoa'}`}>{message}</p>
      )}
    </form>
  );
}
